import moment from "moment";
import { useEffect, useState } from "react";

export type Timeline = {
  description: string;
  file?: string;
  amountPayable: string;
  job_detail_id: string;
  start_date: string;
  duration: string;
};

const useDurationChecker = (
  timelines: Timeline[],
  duration: number,
  budget: number | string
) => {
  const [timelineValidation, setTimelineValidation] = useState<
    "overflow" | "invalid"
  >();
  const [costValidation, setCostValidation] = useState(true);

  const validateTimeline = () => {
    if (timelines.length === 0) return;

    let curDuration = +timelines[0].duration;
    // Only check overflow if duration is a valid positive number (user has set it)
    const validDuration = duration && !isNaN(duration) && duration > 0;
    if (validDuration) {
      if (curDuration > duration) {
        setTimelineValidation("overflow");
        return;
      }
    }

    // Use moment() directly to avoid non-ISO date format deprecation warning
    const presentDate = new Date(moment().format("YYYY-MM-DD")).getTime();

    for (let idx = 1; idx < timelines.length; idx++) {
      let startMoment = moment(timelines[idx].start_date);
      let endMoment = moment(timelines[idx].start_date).add(
        timelines[idx].duration,
        "day"
      );

      let diffDays = startMoment.diff(endMoment, "days");
      curDuration += Math.abs(diffDays);

      if (curDuration > duration) {
        setTimelineValidation("overflow");
        return;
      }

      let prevStartTimeline = new Date(
        moment(timelines[idx - 1].start_date).format("YYYY-MM-DD")
      ).getTime();
      let prevEndTimeline = new Date(
        moment(timelines[idx - 1].start_date)
          .add(timelines[idx - 1].duration, "day")
          .format("YYYY-MM-DD")
      ).getTime();

      let curStartTimeline = new Date(
        startMoment.format("YYYY-MM-DD")
      ).getTime();
      let curEndTimeline = new Date(endMoment.format("YYYY-MM-DD")).getTime();

      if (
        prevEndTimeline > curStartTimeline ||
        prevEndTimeline < presentDate ||
        prevStartTimeline < presentDate ||
        curStartTimeline < presentDate ||
        curEndTimeline < presentDate ||
        curEndTimeline < curStartTimeline ||
        prevEndTimeline < prevStartTimeline
      ) {
        setTimelineValidation("invalid");
        return;
      }
    }

    let prevStartTimeline = new Date(
      moment(timelines[0].start_date).format("YYYY-MM-DD")
    ).getTime();
    let prevEndTimeline = new Date(
      moment(timelines[0].start_date)
        .add(timelines[0].duration, "day")
        .format("YYYY-MM-DD")
    ).getTime();

    if (
      prevEndTimeline < presentDate ||
      prevStartTimeline < presentDate ||
      prevEndTimeline < prevStartTimeline
    ) {
      setTimelineValidation("invalid");
      return;
    }

    setTimelineValidation(undefined);
  };

  const sortTimelines = (timelines: Timeline[]): Timeline[] => {
    const sortedTimelines: Timeline[] = [...timelines];
    sortedTimelines.sort(
      (a, b) =>
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );
    return sortedTimelines;
  };

  useEffect(() => {
    validateTimeline();

    const total = timelines.reduce(
      (total, value) => total + parseInt(value.amountPayable),
      0
    );

    setCostValidation(total <= parseFloat(budget?.toString()));
  }, [timelines, duration, budget]);

  return { timelineValidation, sortTimelines, costValidation };
};

export default useDurationChecker;
